import React, { memo, ChangeEvent, useState, useEffect, useCallback, useContext } from 'react'
import { useSelector } from 'react-redux'
import { Card } from 'transfix-ui/components/Card'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import {
  LoadIdInlineEdit,
  RateInlineEdit,
  MilesInlineEdit,
  CustomerInlineEdit,
  EquipmentInlineEdit,
} from 'apps/fleet-planner/components/form-components'
import { InlineRow } from 'apps/fleet-planner/components/load-details/InlineRow'
import { EQUIPMENT_TYPE } from 'apps/fleet-planner/constants/loads'
import {
  ILoad,
  ILoadEquipment,
  ICustomer,
  ICustomerContact,
  ITemperatureSetting,
} from 'apps/fleet-planner/types'
import { IExternalPermissions } from 'apps/fleet-planner/types/permissions'
import { loadDetailsSelector } from 'apps/fleet-planner/utils/selectors'
import { ShipmentsIcon } from 'transfix-icons'
import { ExternalRate } from './ExternalRate'

interface ILoadDetailsInlineEditProps {
  externalId?: string
  rate?: string
  miles?: string
  trailerType?: EQUIPMENT_TYPE
  trailerLength?: string
  customer?: ICustomer
  customerNotes?: string
  customerId?: string
  customerContact?: ICustomerContact
  customerContactId?: number
  temperatureSetting?: ITemperatureSetting
  currentEditField?: string
  onEditModeRequested(name?: string): void
  onUpdate(changes: Partial<ILoad>): void
  permissions?: IExternalPermissions
  securingOptions?: string
  isExternal?: boolean
  carrierRate?: string
  totalRate?: string
  carrierTotalAccessorialRate?: string
  isQuickpayEnabled?: boolean
  paymentTerms: string
}

export const DetailsHeader = () => (
  <div className='flex flex-row items-center mb4'>
    <ShipmentsIcon size='large' color='coolGray5' />
    <span className='body-medium pl2'>Load Details</span>
  </div>
)

export const LoadDetailsInlineEdit = memo<ILoadDetailsInlineEditProps>(
  ({
    carrierRate,
    carrierTotalAccessorialRate,
    currentEditField,
    customer,
    customerContact,
    customerContactId,
    customerId,
    customerNotes,
    externalId,
    isExternal,
    isQuickpayEnabled,
    miles,
    onEditModeRequested,
    onUpdate,
    paymentTerms,
    permissions,
    rate,
    securingOptions,
    temperatureSetting,
    totalRate,
    trailerLength,
    trailerType,
  }) => {
    const initialLoadDetailsState = {
      customer,
      customerId,
      customerContact,
      customerContactId,
      trailerType,
      trailerLength,
      temperatureSetting,
      externalId,
      rate,
      miles,
    }
    const [loadDetails, setLoadDetails] = useState(initialLoadDetailsState)

    /**
     * Handle situation where component renders before we get the response
     * useEffect to update the component upon receiving response
     * @note this is really costly and causes issues with race conditions!
     * @todo fix
     */
    useEffect(() => {
      setLoadDetails({
        customer,
        customerId,
        customerContact,
        customerContactId,
        trailerType,
        trailerLength,
        temperatureSetting,
        externalId,
        rate,
        miles,
      })
    }, [
      customer,
      customerId,
      customerContact,
      customerContactId,
      trailerType,
      trailerLength,
      temperatureSetting,
      externalId,
      rate,
      miles,
    ])

    const { getFlag } = useContext(FeatureFlagsContext)
    const { enabled: isFpCarrierPortalEnabled } = getFlag('fpCarrierPortal')

    const customerChangeHandler = useCallback((change: Partial<ILoad>) => {
      setLoadDetails(current => ({
        ...current,
        ...change,
      }))
    }, [])

    const externalIdChangeHandler = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      setLoadDetails(current => ({ ...current, externalId: e.target.value }))
    }, [])

    const loadMilesChangeHandler = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      setLoadDetails(current => ({ ...current, miles: e.target.value }))
    }, [])

    const loadRateChangeHandler = useCallback((newRate?: string) => {
      setLoadDetails(current => ({ ...current, rate: newRate }))
    }, [])

    const equipmentChangeHandler = useCallback((change: Partial<ILoadEquipment>) => {
      setLoadDetails(current => ({
        ...current,
        ...change,
      }))
    }, [])

    const saveHandler = useCallback(() => {
      onUpdate({ ...loadDetails })
      onEditModeRequested('')
    }, [loadDetails, onEditModeRequested, onUpdate])

    const cancelHandler = useCallback(() => {
      setLoadDetails(initialLoadDetailsState)
      onEditModeRequested('')
    }, [initialLoadDetailsState, onEditModeRequested])

    const { loading: fpLoading } = useSelector(loadDetailsSelector)
    const { isLoading: mpLoading } = useMarketplaceContext()

    return (
      <Card className='mt4 mb4'>
        <DetailsHeader />
        <InlineRow
          rowTitle='Customer'
          rowBody={
            <CustomerInlineEdit
              customerNotes={customerNotes}
              isLoading={fpLoading}
              isExternal={isExternal}
              isLocked={permissions?.customers}
              customer={customer}
              customerContact={customerContact}
              currentEditField={currentEditField}
              onEditModeRequested={onEditModeRequested}
              onChange={customerChangeHandler}
              onSave={saveHandler}
              onCancel={cancelHandler}
            />
          }
        />
        <InlineRow
          rowTitle='Load ID #'
          rowBody={
            <LoadIdInlineEdit
              isLoading={fpLoading}
              isLocked={permissions?.loadId}
              loadId={loadDetails.externalId}
              currentEditField={currentEditField}
              onEditModeRequested={onEditModeRequested}
              onChange={externalIdChangeHandler}
              onSave={saveHandler}
              onCancel={cancelHandler}
            />
          }
        />
        {/* If feature flag is enabled only display when load is non Transfix */}
        {(isFpCarrierPortalEnabled ? !isExternal : true) && (
          <InlineRow
            rowTitle='Rate'
            rowBody={
              <RateInlineEdit
                isLoading={fpLoading}
                isLocked={permissions?.rate}
                rate={loadDetails.rate}
                currentEditField={currentEditField}
                onEditModeRequested={onEditModeRequested}
                onChange={loadRateChangeHandler}
                onSave={saveHandler}
                onCancel={cancelHandler}
              />
            }
          />
        )}
        {isExternal && isFpCarrierPortalEnabled && (
          <InlineRow
            rowTitle='Rate'
            rowBody={
              <ExternalRate
                isLoading={mpLoading}
                paymentTerms={paymentTerms}
                isQuickpayEnabled={isQuickpayEnabled}
                carrierTotalAccessorialRate={carrierTotalAccessorialRate}
                carrierRate={carrierRate}
                totalRate={totalRate}
              />
            }
          />
        )}
        <InlineRow
          rowTitle='Miles'
          rowBody={
            <MilesInlineEdit
              isLoading={fpLoading}
              isLocked={permissions?.miles}
              miles={loadDetails.miles}
              currentEditField={currentEditField}
              onEditModeRequested={onEditModeRequested}
              onChange={loadMilesChangeHandler}
              onSave={saveHandler}
              onCancel={cancelHandler}
            />
          }
        />
        <InlineRow
          rowTitle='Equipment'
          rowBody={
            <EquipmentInlineEdit
              isLoading={fpLoading}
              securingOptions={securingOptions}
              isLocked={permissions?.equipment}
              trailerType={loadDetails?.trailerType}
              trailerLength={loadDetails?.trailerLength}
              temperatureSetting={loadDetails?.temperatureSetting}
              onChange={equipmentChangeHandler}
              currentEditField={currentEditField}
              onEditModeRequested={onEditModeRequested}
              onSave={saveHandler}
              onCancel={cancelHandler}
            />
          }
        />
      </Card>
    )
  }
)
